import type { WrittenPage } from '../../sections'

/**
 * "The Language of Change: Mastering Communication for Transformation Success"
 * e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "storytelling", "case study", "framework" gibi yerleşik
 * terimler de öyle. 4A ve 3H modellerinin parantez içindeki adları da
 * İngilizce — kitapta öyle geçiyorlar.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
const EBOOK_PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '50164e94-2178-4740-a318-0e941e4174ce-Mastering%20Communication%20for%20Transformation%20Success.pdf'

export const ebookMasteringCommunication: WrittenPage = {
  navLabel: 'Mastering Communication for Transformation Success',
  title: 'Mastering Communication for Transformation Success',
  description:
    'Değişimin dili: insanlarınızı değişimin her aşamasında netlik ve amaçla hizalamanın, '
    + 'ilham vermenin ve harekete geçirmenin yolu. Digitopia Impact Labs e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Değişimin dili:',
      headline: 'Mastering Communication for Transformation Success',
      body: [
        'İletişim olmadan dönüşüm başarısız olur. İnsanlarınızı değişimin her aşamasında netlik '
          + 've amaçla nasıl hizalayacağınızı, onlara nasıl ilham vereceğinizi ve nasıl harekete '
          + 'geçireceğinizi keşfedin.',
      ],
      ctas: [{ label: 'İndirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/language-of-change-ebook-artwork.webp',
        alt:
          'E-kitabın kapak görseli: turuncu ve turkuaz lekelerin önünde, kırmızı tepeli ve mavi '
          + 'mekanik zırhlı bir papağan.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Neden indirmelisiniz?',
      body: [
        'İletişim dönüşümde çoğu zaman gözden kaçar; oysa hizalanma ile kaos, ivme ile duraklama '
          + 'arasındaki farkı o belirler. Bu e-kitap şunlar için pratik bir rehber:',
      ],
      points: [
        'Çalışanları netlik ve empatiyle sürece katmak',
        'Paydaşları birleştiren anlatılar kurmak',
        'En sık yapılan iletişim hatalarından kaçınmak',
        'Storytelling, güven ve stratejik mesajla gerçek dönüşümü sürüklemek',
      ],
      outro: [
        'İster dijital, ister kültürel, ister operasyonel ya da sürdürülebilirlik dönüşümü '
          + 'yönetin, bu kaynak değişimi önemsendiği belli olacak şekilde anlatmanıza yardım '
          + 'eder. Çünkü gerçekten önemli.',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/language-of-change-ebook-covers.webp',
        alt:
          'Mastering Communication for Transformation Success’in biri diğerinin arkasında duran '
          + 'iki kopyası, açık renkli bir zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler öğreneceksiniz?',
      points: [
        'İç iletişimi kurgulamak için kanıtlanmış framework’ler: 4A Modeli (Awareness, Alignment, '
          + 'Activation, Advocacy) ve 3H Yaklaşımı (Head, Heart, Hands)',
        'Kurumun her kademesinde harekete geçiren güçlü dönüşüm anlatıları nasıl yazılır',
        'LEGO, GE, Wolters Kluwer ve daha fazlasından iyi uygulamalar ve gerçek case study’ler',
        'Yaratıcı metafor ve temalar katılımı nasıl yükseltir: “The Hero’s Journey”den “Voyage '
          + 'to Value”ya',
        'Dönüşümü raydan çıkaran en yaygın iletişim hatalarından kaçınma taktikleri',
        'İletişiminizin etkisini izlemek ve iyileştirmek için metrikler',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: EBOOK_PDF }],
      photo: {
        src: '/photo/language-of-change-ebook-stack.webp',
        alt:
          'Üst üste dizilmiş Mastering Communication for Transformation Success baskıları, '
          + 'yukarıdan görünüm.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'İletişimi süper gücünüze dönüştürmeye hazır mısınız?',
      sub: 'Ücretsiz e-kitabı indirin, doğru sözcüklerin gerçek değişimi nasıl sürüklediğini görün.',
      cta: { label: 'E-kitabı indirin', href: EBOOK_PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Digitopia’nın araçlarını kullanmak bizim için son derece değerli oldu. DMI ve Impact '
            + 'Platform, dijital dönüşüm için net ve yapılandırılmış bir yol haritasına '
            + 'kavuşmamızı sağladı. İK, satış ve IT’den çapraz fonksiyonlu ekipleri sürece '
            + 'katarak önceliklerde hizalandık, şirket genelinde farkındalık yarattık ve değişimi '
            + 'etkili biçimde sürükleyecek kilit projeleri belirledik.',
          source: 'Aydın Şener, Dijital Dönüşüm ve Endüstri 4.0 Müdürü',
          company: 'Akçansa',
          logo: { name: 'Akçansa', src: '/logo/akcansacard.png' },
          portrait: { src: '/photo/portrait-aydin-sener.png', alt: 'Aydın Şener' },
        },
      ],
    },
  ],
}
